import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LINE_STATES, AT_STATES, TIPOS, PRIORIDADES, tempoDesde, eur, numRef } from "@/lib/constants";
import { Car, Clock, Plus, Sparkles, AlertTriangle, Hand, CheckCircle2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmarEnvio } from "@/components/ConfirmarEnvio";
import { ContactarFornecedor } from "@/components/ContactarFornecedor";
import { CopilotChegadas, totalChegadas } from "@/components/CopilotChegadas";
import { OrigemTag } from "@/components/FornTag";
import { DispensarX, CopilotLembretes } from "@/components/CopilotDispensar";
import { PeriodoFiltro, CarregarMais } from "@/components/PeriodoFiltro";
function atTotal(a) {
  return (a.linhas || []).reduce((s, l) => s + (l.total || 0), 0);
}

const FLUXO = ["pendente", "devolvido", "em_cotacao", "aguarda_resposta", "aprovado", "aguarda_fornecedor", "em_transito", "recebido", "faturado", "entregue", "creditado", "cancelado"];
const estadoAtrasadoKey = (a) => {
  const ests = (a.linhas || []).map((l) => l.estado).filter((e) => FLUXO.includes(e));
  const ativos = ests.filter((e) => e !== "cancelado");
  const pool = ativos.length ? ativos : ests;
  return pool.length ? pool.reduce((m, e) => (FLUXO.indexOf(e) < FLUXO.indexOf(m) ? e : m)) : null;
};
const estadoAtrasado = (a) => LINE_STATES[estadoAtrasadoKey(a)]?.strip || AT_STATES[a.estado]?.strip || "bg-slate-300";
const ESPERA_LONGA_MS = 3 * 24 * 3600 * 1000;
const esperaLonga = (a) => !["concluido", "arquivado"].includes(a.estado) && !!a.last_contact_at && Date.now() - new Date(a.last_contact_at).getTime() > ESPERA_LONGA_MS;

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "urgentes", label: "Urgentes" },
  { key: "pendente", label: "Pendentes" },
  { key: "em_cotacao", label: "Em Cotação" },
  { key: "aguarda_resposta", label: "Aguarda resposta" },
  { key: "concluidos", label: "Concluídos" },
  { key: "arquivados", label: "Arquivados" },
  { key: "dev_garantia", label: "Dev/Garantia" },
];

export default function Dashboard() {
  const [ats, setAts] = useState([]);
  const [concluidos, setConcluidos] = useState([]);
  const [arquivados, setArquivados] = useState([]);
  const [concPeriodo, setConcPeriodo] = useState({ desde: "", ate: "" });
  const [concMore, setConcMore] = useState(false);
  const CONC_PAGE = 50;
  const loadConcluidos = async (append = false, per = concPeriodo) => {
    const { data } = await api.get("/atendimentos", { params: {
      estado: "concluido", limit: CONC_PAGE, skip: append ? concluidos.length : 0,
      desde: per.desde || undefined, ate: per.ate || undefined } });
    setConcluidos(append ? [...concluidos, ...data] : data);
    setConcMore(data.length === CONC_PAGE);
  };
  const [stats, setStats] = useState(null);
  const [copilot, setCopilot] = useState(null);
  const [ordem, setOrdem] = useState(() => localStorage.getItem("atlas_ordem_pedidos") || "recentes");
  const changeOrdem = (k) => { setOrdem(k); localStorage.setItem("atlas_ordem_pedidos", k); };
  const [filter, setFilter] = useState("todos");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const [a, s, c] = await Promise.all([
      api.get("/atendimentos"),
      api.get("/dashboard/stats"),
      api.get("/copilot/sugestoes"),
    ]);
    setAts(a.data);
    setStats(s.data);
    setCopilot(c.data);
    loadConcluidos();
    if (a.data.length && !selected) setSelected(a.data[0]);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const reloadCopilot = () => api.get("/copilot/sugestoes").then((r) => setCopilot(r.data));

  const BASE_KEYS = ["todos", "urgentes", "pendente", "em_cotacao", "aguarda_resposta", "concluidos", "arquivados", "dev_garantia"];
  const isEstadoPick = !BASE_KEYS.includes(filter);
  useEffect(() => {
    if (filter === "arquivados") { api.get("/atendimentos", { params: { estado: "arquivado", limit: 300 } }).then((r) => { setArquivados(r.data); setSelected(r.data[0] || null); }); }
  }, [filter]);

  const revertRef = useRef(null);
  useEffect(() => {
    if (revertRef.current) { clearTimeout(revertRef.current); revertRef.current = null; }
    if (!BASE_KEYS.includes(filter)) {
      revertRef.current = setTimeout(() => setFilter("todos"), 180000);
    }
    return () => { if (revertRef.current) clearTimeout(revertRef.current); };
    /* eslint-disable-next-line */
  }, [filter]);

  const listSource = filter === "concluidos" ? concluidos : filter === "arquivados" ? arquivados : (isEstadoPick ? [...ats, ...concluidos] : ats);
  const filtered = listSource.filter((a) => {
    if (filter === "todos" || filter === "concluidos" || filter === "arquivados") return true;
    if (filter === "urgentes") return a.prioridade === 1;
    if (filter === "dev_garantia") return a.tipo === "devolucao" || a.tipo === "garantia";
    return a.estado === filter || (a.linhas || []).some((l) => l.estado === filter);
  }).sort((x, y) => ordem === "espera"
    ? (x.last_contact_at || x.created_at || "").localeCompare(y.last_contact_at || y.created_at || "")
    : (y.created_at || "").localeCompare(x.created_at || "") || (y.numero || 0) - (x.numero || 0));

  const hasSuggestions =
    copilot &&
    ((copilot.alertas_fecho?.length || 0) +
      totalChegadas(copilot.previsoes) +
      (copilot.fechos?.length || 0) +
      (copilot.follow_ups?.length || 0) +
      (copilot.lembretes?.length || 0) +
      (copilot.devolucoes_garantias?.length || 0) +
      (copilot.conflitos_matricula?.length || 0) >
      0);

  return (
    <div className="space-y-4">
      {/* TOP: Copilot — full-width highlighted bar */}
      <div className="bg-gradient-to-r from-sky-600 to-indigo-600 rounded-xl shadow-md overflow-hidden" data-testid="copilot-panel">
        <div className="flex items-center gap-2 px-5 py-2.5 border-b border-white/15">
          <Sparkles size={16} className="text-white" />
          <h3 className="text-sm font-bold uppercase tracking-wide text-white">Copilot · Sugestões</h3>
          {copilot?.total > 0 && (
            <span className="ml-1 text-[10px] font-bold bg-white text-sky-700 rounded-full px-2 py-0.5" data-testid="copilot-count">{copilot.total}</span>
          )}
          {!hasSuggestions && copilot && (
            <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-white/90" data-testid="copilot-empty">
              <CheckCircle2 size={14} /> Tudo em dia · sem sugestões
            </span>
          )}
        </div>

        {hasSuggestions && (
          <div className="bg-white/95 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {copilot?.alertas_fecho?.length > 0 && (
                <div data-testid="copilot-group-alertas-fecho">
                  <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5 flex items-center gap-1"><AlertTriangle size={11} className="text-rose-500" /> Fecho a aproximar-se</div>
                  <div className="space-y-1">
                    {copilot.alertas_fecho.map((f, i) => (
                      <div key={i} className="flex items-stretch gap-1">
                        <button onClick={() => navigate("/por-encomendar")} data-testid={`copilot-alerta-fecho-${i}`} className="flex-1 text-left text-xs bg-rose-50 border border-rose-200 rounded-md p-2 hover:border-rose-400 transition-colors">
                          Faltam <b>{f.minutos} min</b> para o fecho da {f.periodo === "manha" ? "manhã" : "tarde"} ({f.hora}) de <b><OrigemTag forn={f.fornecedor ? { nome: f.fornecedor, codigo_interno: f.fornecedor_codigo } : null} origem={{ nome: f.origem, codigo: f.codigo }} /></b> — <b>{f.linhas}</b> linha(s) por enviar
                        </button>
                        {f.origem_id && <ConfirmarEnvio origemId={f.origem_id} onDone={reloadCopilot} testid={`copilot-confirmar-alerta-${i}`} />}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <CopilotChegadas previsoes={copilot?.previsoes} />

              {copilot?.fechos?.length > 0 && (
                <div data-testid="copilot-group-fechos">
                  <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5 flex items-center gap-1"><Clock size={11} className="text-sky-500" /> Fecho de encomendas hoje</div>
                  <div className="space-y-1">
                    {copilot.fechos.map((f, i) => (
                      <div key={i} role="button" tabIndex={0} onClick={() => navigate("/por-encomendar")} className="text-xs bg-slate-50 border border-slate-200 rounded-md p-2 flex items-start gap-2 cursor-pointer hover:border-slate-400 transition-colors" data-testid={`copilot-fecho-${i}`}>
                        <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800"><span title={f.origem} className="cursor-help">{f.codigo || f.origem}</span> <span className="font-normal text-slate-400">{f.modo === "recolha" ? "· recolha" : (f.transportadora ? `· ${f.transportadora}` : "")}</span></div>
                        <div className="flex gap-3 mt-0.5">
                          {f.hora_fecho_manha && <span className={f.manha_passou ? "text-rose-500 line-through" : "text-emerald-700"}>manhã {f.hora_fecho_manha}</span>}
                          {f.hora_fecho_tarde && <span className={f.tarde_passou ? "text-rose-500 line-through" : "text-emerald-700"}>tarde {f.hora_fecho_tarde}</span>}
                        </div>
                        {f.por_enviar > 0 && <div className="text-[10px] text-amber-700 mt-0.5">{f.por_enviar} linha(s) por enviar</div>}
                        </div>
                        {f.por_enviar > 0 && f.origem_id && <span onClick={(ev) => ev.stopPropagation()}><ConfirmarEnvio origemId={f.origem_id} onDone={reloadCopilot} testid={`copilot-confirmar-fecho-${i}`} /></span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <CopilotLembretes lembretes={copilot?.lembretes} onDone={reloadCopilot} />

              {copilot?.follow_ups?.length > 0 && (
                <div data-testid="copilot-group-followups">
                  <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5 flex items-center gap-1">
                    <Clock size={11} /> Aguarda resposta (≥ {copilot.dias_min} dias)
                  </div>
                  <div className="space-y-1.5">
                    {copilot.follow_ups.map((f) => (
                      <div key={f.atendimento_id} className="relative group">
                      <button
                        onClick={() => navigate(`/pedido/${f.atendimento_id}`)}
                        data-testid={`copilot-followup-${f.numero}`}
                        className="w-full text-left bg-white rounded-md border border-violet-200 p-2 pr-7 hover:border-violet-400 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-800">{numRef(f.numero, f.codigo)} · {f.cliente || "—"}</span>
                          <span className="text-[10px] font-bold text-violet-700 bg-violet-100 rounded px-1.5">{f.dias} dias</span>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">Seguir: {f.linhas.join(", ")}</div>
                        <div className="text-[10px] text-slate-400" data-testid={`copilot-followup-arquiva-${f.numero}`}>{f.arquiva_em > 0 ? `Arquiva sozinho dentro de ${f.arquiva_em} dia(s)` : "Arquiva na próxima verificação"}</div>
                      </button>
                      <DispensarX chave={f.chave} onDone={reloadCopilot} testid={`copilot-followup-fechar-${f.numero}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {copilot?.devolucoes_garantias?.length > 0 && (
                <div data-testid="copilot-group-dg">
                  <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5 flex items-center gap-1">
                    <RotateCcw size={11} className="text-orange-500" /> Devoluções / Garantias em aberto
                  </div>
                  <div className="space-y-1.5">
                    {copilot.devolucoes_garantias.map((d) => (
                      <button key={d.atendimento_id} onClick={() => navigate(`/pedido/${d.atendimento_id}`)}
                        data-testid={`copilot-dg-${d.numero}`}
                        className="w-full text-left bg-orange-50 border border-orange-200 rounded-md p-2 hover:border-orange-400 transition-colors">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-800">{numRef(d.numero, d.codigo)} · {d.tipo === "garantia" ? "Garantia" : "Devolução"}</span>
                          {d.aguarda && <span className="text-[9px] font-bold text-orange-700 bg-orange-100 rounded px-1.5">aguarda</span>}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{d.cliente || "—"}{d.origem_numero ? ` · do ${numRef(d.origem_numero, d.origem_codigo)}` : ""}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {copilot?.conflitos_matricula?.length > 0 && (
                <div data-testid="copilot-group-conflitos-mat">
                  <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5 flex items-center gap-1">
                    <AlertTriangle size={11} className="text-rose-500" /> Conflitos de matrícula
                  </div>
                  <div className="space-y-1.5">
                    {copilot.conflitos_matricula.map((c) => (
                      <div key={c.matricula} className="bg-rose-50 border border-rose-200 rounded-md p-2" data-testid={`copilot-conflito-mat-${c.matricula}`}>
                        <span className="plate">{c.matricula}</span>
                        <div className="text-[11px] text-rose-700 mt-1">{c.detalhes.join(" / ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT: prioritized list */}
        <div className="col-span-12 lg:col-span-3 space-y-3">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 whitespace-nowrap">{filter === "arquivados" ? "Pedidos arquivados" : filter === "concluidos" ? "Pedidos concluídos" : "Pedidos ativos"}</h2>
            <span className="text-xs text-slate-400">{filtered.length}</span>
            <div className="ml-auto inline-flex bg-slate-100 rounded-md p-0.5" data-testid="ordem-toggle">
              {[["recentes", "Mais recentes"], ["espera", "Sem contacto há mais tempo"]].map(([k, label]) => (
                <button key={k} onClick={() => changeOrdem(k)} data-testid={`ordem-${k}`}
                  className={`text-[11px] px-2 py-0.5 rounded whitespace-nowrap transition-colors ${ordem === k ? "bg-white text-sky-700 shadow-sm font-semibold" : "text-slate-500 hover:text-slate-800"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => { setFilter(f.key); setSelected(null); setTimeout(() => { document.querySelector("[data-testid^='pedido-card-']")?.click(); }, 50); }}
                data-testid={`filter-${f.key}`}
                className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                  filter === f.key ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-[calc(100vh-11rem)] overflow-y-auto pr-1">
            {filtered.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelected(a)}
                data-testid={`pedido-card-${a.numero}`}
                className={`w-full text-left rounded-lg border shadow-sm hover:shadow-md transition-shadow relative overflow-hidden ${
                  esperaLonga(a) ? "bg-rose-50/70" : "bg-white"} ${
                  selected?.id === a.id ? "border-sky-400 ring-1 ring-sky-300" : esperaLonga(a) ? "border-rose-200" : "border-slate-200"
                }`}
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${estadoAtrasado(a)}`} title={LINE_STATES[estadoAtrasadoKey(a)]?.label} data-testid={`card-strip-${a.numero}`} />
                <div className="p-3 pl-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-slate-700">{numRef(a.numero, a.codigo)}</span>
                    <span className={`text-[10px] ${PRIORIDADES[a.prioridade]?.cls}`}>{PRIORIDADES[a.prioridade]?.label}</span>
                  </div>
                  {a.tipo && TIPOS[a.tipo] && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] font-semibold mb-1 ${TIPOS[a.tipo].badge}`}>
                      {TIPOS[a.tipo].label}
                    </span>
                  )}
                  <div className="text-sm font-semibold text-slate-800 truncate">{a.cliente?.nome || "Sem cliente"}</div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                    {a.veiculo?.matricula && <span className="plate">{a.veiculo.matricula}</span>}
                    <span className="flex items-center gap-1"><Car size={12} />{(a.linhas || []).length}</span>
                    {(a.veiculo?.marca || a.veiculo?.modelo) && (
                      <span className="truncate min-w-0 text-slate-500" title={`${a.veiculo?.marca || ""} ${a.veiculo?.modelo || ""}`.trim()} data-testid={`card-viatura-${a.numero}`}>
                        {[a.veiculo?.marca ? `${a.veiculo.marca.trim().charAt(0)}.` : "", a.veiculo?.modelo || ""].join(" ").trim()}
                      </span>
                    )}
                    <span className={`flex items-center gap-1 ml-auto ${esperaLonga(a) ? "text-rose-600 font-semibold" : ""}`} title={esperaLonga(a) ? "Sem contacto há mais de 3 dias" : undefined} data-testid={esperaLonga(a) ? `card-espera-longa-${a.numero}` : undefined}><Clock size={12} />{tempoDesde(a.last_contact_at)}</span>
                  </div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Sem pedidos.</div>}
            {filter === "concluidos" && (
              <div className="space-y-2 pt-1">
                <PeriodoFiltro desde={concPeriodo.desde} ate={concPeriodo.ate} testid="conc-periodo"
                  onChange={(p) => { setConcPeriodo(p); loadConcluidos(false, p); }} />
                <CarregarMais visible={concMore} onClick={() => loadConcluidos(true)} testid="conc-carregar-mais" />
              </div>
            )}
          </div>
        </div>

        {/* CENTER: work area */}
        <div className="col-span-12 lg:col-span-6">
          {selected ? (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm fade-up" key={selected.id}>
              <div className="bg-sky-50 border-b border-sky-100 px-5 py-3 rounded-t-lg flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Pedido de cotação / Atendimento</div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-lg font-bold text-slate-900">{numRef(selected.numero, selected.codigo)}</span>
                    {selected.tipo && TIPOS[selected.tipo] && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${TIPOS[selected.tipo].badge}`}>
                        {TIPOS[selected.tipo].label}
                      </span>
                    )}
                    {selected.veiculo?.matricula && <span className="plate">{selected.veiculo.matricula}</span>}
                    <span className="text-sm text-slate-600">{selected.veiculo?.marca} {selected.veiculo?.modelo}</span>
                  </div>
                </div>
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700" data-testid="btn-abrir-pedido"
                  onClick={() => navigate(`/pedido/${selected.id}`)}>Abrir</Button>
              </div>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-5 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Cliente</div>
                  <div className="font-medium text-slate-800">{selected.cliente?.nome || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Tipo</div>
                  <div className="text-slate-700 capitalize">{selected.cliente?.tipo || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Responsável</div>
                  <div className="text-slate-700">{selected.responsavel_atual}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Total</div>
                  <div className="font-bold text-slate-900">{eur(atTotal(selected))}</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Qtd</th>
                      <th className="text-left px-3 py-2 font-semibold">Descrição</th>
                      <th className="text-left px-3 py-2 font-semibold">Referência</th>
                      <th className="text-right px-3 py-2 font-semibold">P. Unit</th>
                      <th className="text-right px-3 py-2 font-semibold">Desc</th>
                      <th className="text-right px-3 py-2 font-semibold">Total</th>
                      <th className="text-left px-3 py-2 font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.linhas || []).map((l) => (
                      <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2">{l.qtd}</td>
                        <td className="px-3 py-2 font-medium text-slate-800">{l.descricao}</td>
                        <td className="px-3 py-2 font-mono text-slate-600">{l.ref_marca || "—"}</td>
                        <td className="px-3 py-2 text-right">{eur(l.preco_unit)}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{l.desconto ? `${l.desconto}%` : "—"}</td>
                        <td className="px-3 py-2 text-right font-semibold">{eur(l.total)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${LINE_STATES[l.estado]?.badge}`}>
                            {LINE_STATES[l.estado]?.label || l.estado}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-dashed border-slate-300 h-96 flex flex-col items-center justify-center text-slate-400">
              <Plus size={28} className="mb-2" />
              <p className="text-sm">Selecione ou crie um pedido.</p>
            </div>
          )}
        </div>

        {/* RIGHT: counts */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Linhas por estado</h3>
              {isEstadoPick && (
                <button onClick={() => setFilter("todos")} data-testid="estado-box-reset"
                  className="text-[10px] font-medium text-sky-600 hover:underline">voltar à cronologia</button>
              )}
            </div>
            {isEstadoPick && (
              <div className="text-[10px] text-slate-400 mb-2">A mostrar só os pedidos com linhas em «{LINE_STATES[filter]?.label || filter}». Reverte automaticamente.</div>
            )}
            <div className="space-y-1">
              {stats && Object.entries(stats.counts).map(([k, v]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`estado-box-${k}`}
                  className={`w-full flex items-center gap-2 text-sm rounded-md px-2 py-1 transition-colors ${filter === k ? "bg-sky-50 ring-1 ring-sky-300" : "hover:bg-slate-50"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${LINE_STATES[k]?.strip || "bg-slate-300"}`} />
                  <span className="text-slate-600">{LINE_STATES[k]?.label || k}</span>
                  <span className="ml-auto font-semibold text-slate-800">{v}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Resumo do dia — compact strip (fundo da página) */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="resumo-strip">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Resumo do dia</span>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-sky-700" data-testid="stat-abertos">{stats?.abertos ?? "—"}</span>
          <span className="text-[11px] text-slate-500">Pedidos abertos</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-emerald-700">{stats ? eur(stats.volume_dia) : "—"}</span>
          <span className="text-[11px] text-slate-500">Volume do dia</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-slate-700" data-testid="stat-entregues-mes">{stats?.entregues_mes ?? "—"}</span>
          <span className="text-[11px] text-slate-500">Entregues {stats?.mes_label || ""}</span>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <span className="text-lg font-bold text-indigo-700" data-testid="stat-encomendas">{stats?.total_encomendas ?? "—"}</span>
          <span className="text-[11px] text-slate-500">Encomendas abertas</span>
        </div>
      </div>
    </div>
  );
}
